import React, { useState } from 'react';

interface MemberScreenProps {
  member: import('./EditMemberForm').Member;
  onLogout: () => void;
  accessToken?: string | null;
}

const MemberScreen: React.FC<MemberScreenProps> = ({ member, onLogout, accessToken }) => {
  const [showReset, setShowReset] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage('');
    if (!oldPassword || !newPassword || !confirmPassword) {
      setMessage('All fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('New passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`/api/member/change_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ email: member.email, old_password: oldPassword, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Password changed successfully!');
        setShowReset(false);
      } else {
        setMessage(data.message || 'Failed to change password.');
      }
    } catch (err) {
      setMessage('An error occurred.');
    } finally {
      setLoading(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <div className="py-8 relative">
      <button
        onClick={onLogout}
        className="absolute top-0 right-0 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md shadow-lg mt-2 mr-2"
      >
        Logout
      </button>
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Welcome, {member?.name}</h2>
  <p>Email: {member?.email}</p>
  <p>NFC Token ID: {member?.nfc_token_id || 'N/A'}</p>
  <p>Credits: {member?.credits}</p>
  <p>Sports: {member.sports && member.sports.length > 0 ? member.sports.map(s => s.name).join(', ') : 'None'}</p>
      <button
        className="mt-6 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow-lg transition duration-300 ease-in-out"
        onClick={() => setShowReset((v) => !v)}
      >
        {showReset ? 'Cancel Password Change' : 'Change Password'}
      </button>
      {showReset && (
        <form onSubmit={handlePasswordReset} className="mt-6 max-w-md mx-auto bg-white p-6 rounded-lg shadow-md">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Old Password</label>
            <input
              type="password"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">New Password</label>
            <input
              type="password"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Confirm New Password</label>
            <input
              type="password"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          {message && <div className="mb-4 text-red-600">{message}</div>}
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline shadow-lg transition duration-300 ease-in-out"
            disabled={loading}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      )}
    </div>
  );
};

export default MemberScreen;
